import Input from "app/components/Input";
import { Popover, PopoverAnchor, PopoverContent } from "app/components/Popover";
import clsx from "clsx";
import React, { FC, useEffect, useRef, useState } from "react";
import { isoToTimeSlot, parseTime } from "./helpers";
import { TimeDropdownProps, TimeSlot } from "./types";

export const TimeDropdown: FC<TimeDropdownProps> = ({
  value,
  onChange,
  inputClassName,
  className,
  mode,
  slots,
  futureDays,
}) => {
  const selectedRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSelectingRef = useRef(false);

  const [focused, setFocused] = useState(false);
  const [inputText, setInputText] = useState(value);

  useEffect(() => {
    setInputText(value);
  }, [value]);

  const open = focused;

  const handleSelect = (slot: TimeSlot) => {
    isSelectingRef.current = true;
    onChange(slot);
    setInputText(isoToTimeSlot(slot.iso));
    setFocused(false);
    inputRef.current?.blur();
  };

  const commitInput = () => {
    const parsed = parseTime(inputText);
    if (!parsed) {
      setInputText(value);
      return;
    }
    const matched = slots.find((s) => isoToTimeSlot(s.iso) === parsed.label);
    const finalSlot = matched || parsed;
    onChange(finalSlot);
    setInputText(finalSlot.label);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isSelectingRef.current = false;
    setInputText(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitInput();
      setFocused(false);
      inputRef.current?.blur();
    }
  };

  const handleFocus = () => {
    setFocused(true);
    setTimeout(() => {
      selectedRef.current?.scrollIntoView({ block: "center" });
    }, 0);
  };

  const handleBlur = () => {
    setFocused(false);
    setTimeout(() => {
      if (isSelectingRef.current) return;
      commitInput();
    }, 150);
  };

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <button
          className={clsx(
            "max-w-24",
            mode === "edit" && "max-w-20",
            "relative",
          )}
        >
          <Input
            id="time"
            inputRef={inputRef}
            type="text"
            inputClassName={clsx(
              "!rounded !h-auto !p-0 !border-none",
              inputClassName,
            )}
            className={clsx(
              "text-sm leading-none !text-black py-2.5 border !border-gray-100 bg-white hover:bg-gray-400 focus-within:border-secondary !text-center",
              mode === "edit" && "!px-[7px] !py-[8px] text-xs",
              className,
            )}
            value={inputText}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />

          {futureDays ? (
            <span className="absolute -right-4 top-0.5 text-[11px] font-normal text-black leading-none">
              {`+${futureDays}`}
            </span>
          ) : null}
        </button>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="!max-w-28 max-h-[320px] overflow-y-auto rounded border border-gray-100 bg-white shadow-md !p-0 !py-2.5"
      >
        {slots?.map((slot) => {
          const isSelected = slot.value === value;
          return (
            <button
              key={slot.value}
              ref={isSelected ? selectedRef : undefined}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(slot)}
              className={clsx(
                "w-full px-4 py-5 text-sm text-black hover:bg-gray-50 focus:outline-none whitespace-nowrap",
              )}
            >
              {slot.label}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
